import { useFormState } from 'informed';
import { useDateNow, useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { getFormattedDate } from 'lib/common';
import { getCourseLanguageVersionOptions } from 'lib/courses';
import { ContentBox } from 'components/common';
import {
  Checkbox,
  FormGroup,
  FormSection,
  LanguageVariants,
  RichText,
  Text
} from 'components/common/form';
import { CourseCompletionCustomContent } from 'components/courses/common';
import { ReaderCompletionFormContent } from 'components/reader/completion';

import type { Course } from 'shared-types';
import type { ReduxRootState } from 'types';
import type { CourseCompletionSettings } from './SettingsCompletion';

interface Props {
  course: Pick<
    Course,
    'customAttributes' | 'language' | 'languageVersions' | 'title'
  >;
  user?: ReduxRootState['user'];
}

const SettingsCompletionForm = ({ course, user }: Props) => {
  const { dateNow } = useDateNow();

  const {
    messages,
    pageState,
    serverConfig: {
      courseCompletionCustomContent,
      courseCompletionHideRequirements
    },
    theme
  } = useReduxState();

  const formState = useFormState<CourseCompletionSettings>();

  const getCustomLocales = () => {
    const options = getCourseLanguageVersionOptions({ course });
    return Object.entries(options).map(([key, value]) => ({
      key,
      label: value.capitalizedName,
      icon: key
    }));
  };

  return (
    <FormSection title={messages.settingsCompletionForm} maxWidth="100%">
      <div>
        <div className="instructions">
          <div className="instructions-settings">
            <FormGroup label={messages.commonInstructions}>
              <Checkbox
                disabled={!formState.values.completionEnabled}
                field="completionFormCustomInstructions.isEnabled"
                id="completion-form-custom-instructions-enabled"
                label={
                  messages.settingsCompletionCustomContentInstructionsEnabled
                }
              />
            </FormGroup>

            <FormGroup label={messages.emailHeading} maxWidth="400px">
              <Text
                disabled={
                  !formState.values.completionEnabled ||
                  !formState.values.completionFormCustomInstructions?.isEnabled
                }
                field="completionFormCustomInstructions.title"
                id="completion-form-custom-instructions-title"
                placeholder={messages.commonAddText}
              />
              {formState.values.completionEnabled &&
                formState.values.completionFormCustomInstructions?.isEnabled &&
                course.languageVersions &&
                Object.keys(course.languageVersions).length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <LanguageVariants
                      customLocales={getCustomLocales()}
                      field="completionFormCustomInstructions.titleLocales"
                      type="text"
                    />
                  </div>
                )}
            </FormGroup>

            <FormGroup label={messages.commonContent} maxWidth="100%">
              <RichText
                disabled={
                  !formState.values.completionEnabled ||
                  !formState.values.completionFormCustomInstructions?.isEnabled
                }
                field="completionFormCustomInstructions.content"
                id="completion-form-custom-instructions-content"
                rows={3}
              />
              {formState.values.completionEnabled &&
                formState.values.completionFormCustomInstructions?.isEnabled &&
                course.languageVersions &&
                Object.keys(course.languageVersions).length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <LanguageVariants
                      customLocales={getCustomLocales()}
                      field="completionFormCustomInstructions.contentLocales"
                      type="richtext"
                    />
                  </div>
                )}
            </FormGroup>

            {!courseCompletionHideRequirements && (
              <Checkbox
                disabled={
                  !formState.values.completionEnabled ||
                  !formState.values.completionFormCustomInstructions?.isEnabled
                }
                field="completionFormCustomInstructions.showRequirements"
                id="completion-form-custom-instructions-show-requirements"
                label={
                  messages.settingsCompletionCustomContentInstructionsShowRequirements
                }
                style={{ marginTop: 10 }}
              />
            )}

            <FormGroup label={messages.commonButtonText} maxWidth="240px">
              <Text
                disabled={
                  !formState.values.completionEnabled ||
                  !formState.values.completionFormCustomInstructions?.isEnabled
                }
                field="completionFormCustomInstructions.buttonText"
                id="completion-form-custom-instructions-button-text"
                placeholder={messages.commonAddText}
              />
              {formState.values.completionEnabled &&
                formState.values.completionFormCustomInstructions?.isEnabled &&
                course.languageVersions &&
                Object.keys(course.languageVersions).length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <LanguageVariants
                      customLocales={getCustomLocales()}
                      field="completionFormCustomInstructions.buttonTextLocales"
                      type="text"
                    />
                  </div>
                )}
            </FormGroup>
          </div>

          <div className="instructions-preview">
            <ContentBox>
              <div
                style={{
                  backgroundColor: 'white',
                  borderRadius: theme.cardBorderRadius,
                  padding: 25
                }}
              >
                <ReaderCompletionFormContent
                  courseData={{
                    course: {
                      completionAllowRetries:
                        formState.values?.completionAllowRetries,
                      completionElectronicSignature:
                        formState.values?.completionElectronicSignature,
                      completionFormCustomInstructions:
                        formState.values?.completionFormCustomInstructions,
                      completionReading: formState.values?.completionReading,
                      completionTasks: formState.values?.completionTasks,
                      completionVideos: formState.values?.completionVideos,
                      enableAdminAnalytics:
                        pageState.course.enableAdminAnalytics
                    },
                    courseRequirements: {
                      numberOfTasks: 1,
                      numberOfVideos: 1,
                      taskMaxPoints: 1
                    }
                  }}
                  fontSize="medium"
                  onSubmit={() => null}
                />
              </div>
            </ContentBox>
          </div>
        </div>

        {courseCompletionCustomContent ? (
          <FormGroup
            label={messages.settingsCompletionFormShowAfterCompletion}
            style={{ marginTop: 40 }}
          >
            <Checkbox
              disabled={!formState.values.completionEnabled}
              field="completionFormShowCustomContent"
              id="completion-form-show-custom-content"
              label={messages.settingsCompletionFormShowCustomContent}
            />
            <div
              className="custom-content"
              style={{
                margin: '30px 15px 15px',
                opacity: formState.values.completionFormShowCustomContent
                  ? 1
                  : 0.5
              }}
            >
              <CourseCompletionCustomContent
                course={course}
                courseCompletion={{
                  completionDate: getFormattedDate({
                    date: dateNow,
                    dateFormat: 'dd.MM.yyyy'
                  }),
                  courseTitle: course.title,
                  expirationDate: null,
                  tasksPercentage: '79%',
                  tasksPoints: '15/19'
                }}
                targetUser={user}
              />
            </div>
          </FormGroup>
        ) : null}

        <style jsx>{`
          .instructions {
            align-items: flex-start;
            display: flex;
            flex-wrap: wrap;
            gap: 30px;
            justify-content: space-between;
            margin-top: 15px;
          }
          .instructions-settings {
            flex-grow: 1;
            flex-shrink: 1;
          }
          .instructions-preview {
            flex-grow: 0;
            flex-shrink: 0;
            max-width: 100%;
            width: 560px;
          }
          @media (min-width: ${breakpoints.huge}px) {
            .instructions {
              flex-wrap: nowrap;
            }
          }
        `}</style>
      </div>
    </FormSection>
  );
};

export default SettingsCompletionForm;
